import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const ClientPortalAuthenticationGateway = () => {
  const navigate = useNavigate();
  const { user, signIn, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [lockoutTime, setLockoutTime] = useState(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (user && !authLoading) {
      navigate('/client-contract-management-portal');
    }
  }, [user, authLoading, navigate]);

  // Handle lockout timer
  useEffect(() => {
    if (lockoutTime) {
      const timer = setTimeout(() => {
        setLockoutTime(null);
        setLoginAttempts(0);
        setError('');
      }, lockoutTime);
      return () => clearTimeout(timer);
    }
  }, [lockoutTime]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    
    // Check if locked out
    if (lockoutTime) {
      setError(`تم قفل الحساب مؤقتاً. يرجى المحاولة بعد ${Math.ceil(lockoutTime / 1000)} ثانية`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      await signIn(email, password);
      
      // Store remember device preference
      if (rememberDevice) {
        localStorage.setItem('rememberDevice', 'true');
      }
      
      // Reset login attempts on success
      setLoginAttempts(0);
      navigate('/client-contract-management-portal');
    } catch (err) {
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      
      // Progressive lockout
      if (newAttempts >= 5) {
        setLockoutTime(60000); // 60 seconds
        setError('تم تجاوز عدد المحاولات المسموح بها. تم قفل الحساب لمدة دقيقة واحدة');
      } else if (newAttempts >= 3) {
        setLockoutTime(10000); // 10 seconds
        setError(`بيانات الدخول غير صحيحة. المحاولة ${newAttempts} من 5. سيتم قفل الحساب بعد ${5 - newAttempts} محاولات`);
      } else {
        setError(err?.message || 'فشل تسجيل الدخول. يرجى التحقق من البريد الإلكتروني وكلمة المرور');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    // Navigate to password reset (placeholder)
    alert('سيتم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4" dir="rtl">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23DAA520" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          backgroundSize: '60px 60px'
        }}></div>
      </div>
      {/* Login Card */}
      <div className="relative w-full max-w-md">
        {/* Company Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 shadow-warm-lg mb-4">
            <Icon name="Building2" size={40} color="#000" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">بوابة العملاء</h1>
          <p className="text-gray-400">الوصول الآمن إلى عقودك ومستندات الملكية</p>
        </div>

        {/* Login Form Card */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-warm-xl border border-gray-700/50 p-8">
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Email Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                البريد الإلكتروني
              </label>
              <div className="relative">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  placeholder="أدخل بريدك الإلكتروني"
                  required
                  disabled={loading || lockoutTime}
                  className="w-full pr-12 bg-gray-900/50 border-gray-600 text-white placeholder-gray-500 focus:border-amber-500"
                />
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <Icon name="Mail" size={20} color="#9CA3AF" />
                </div>
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                كلمة المرور
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  placeholder="أدخل كلمة المرور"
                  required
                  disabled={loading || lockoutTime}
                  className="w-full pr-12 bg-gray-900/50 border-gray-600 text-white placeholder-gray-500 focus:border-amber-500"
                />
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <Icon name="Lock" size={20} color="#9CA3AF" />
                </div>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-amber-500 transition-colors"
                  disabled={loading || lockoutTime}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>
            </div>

            {/* Remember Device & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e?.target?.checked)}
                  disabled={loading || lockoutTime}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-900/50 text-amber-500 focus:ring-amber-500 focus:ring-offset-gray-800"
                />
                <span className="text-sm text-gray-400">تذكر هذا الجهاز</span>
              </label>
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm text-amber-500 hover:text-amber-400 transition-colors"
                disabled={loading || lockoutTime}
              >
                نسيت كلمة المرور؟
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <Icon name="AlertCircle" size={20} color="#EF4444" className="flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {/* Login Button */}
            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={loading || lockoutTime}
              className="h-12 text-base font-semibold"
            >
              {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
            </Button>
          </form>

          {/* Security Features */}
          <div className="mt-6 pt-6 border-t border-gray-700/50">
            <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Icon name="Shield" size={14} color="#10B981" />
                <span>اتصال آمن</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Lock" size={14} color="#10B981" />
                <span>تشفير 256-bit</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="CheckCircle" size={14} color="#10B981" />
                <span>معتمد</span>
              </div>
            </div>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            تواجه مشكلة في تسجيل الدخول؟{' '}
            <button className="text-amber-500 hover:text-amber-400 transition-colors">
              اتصل بالدعم الفني
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientPortalAuthenticationGateway;